import { gql } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LOGIN_PATH } from 'src/common/constants';
import images from 'src/common/images';
import { ErrorMstProduct, MstProduct, MutationSet } from 'src/common/interfaces';
import { RecordState } from 'src/common/string-enum';
import { ErrorMessage, InfoMessage, InfoWord } from 'src/enum/messsage-enum';
import { deleteMstProduct, insertMstProduct, updateMstProduct } from 'src/graphql/mutations';
import { getMstProduct } from 'src/graphql/queries';
import { useAppLazyQuery, useAppMutation } from 'src/libs/hooks';
import utility from 'src/libs/utility';
import MainLayout from '../main-layout';

const defaultErrorMstProduct = {
  isErrorProductCd: false,
  isErrorProductNm: false,
  isErrorMaintenanceLimit: false,
};

const emptyProduct = {
  mpr_product_cd: '',
  mpr_product_nm: '',
  mpr_discount_type: null,
  mpr_maintenance_limit: null,
  mpr_available_kb: null,
  isCannotEditProductCd: false,
  recordState: RecordState.INSERT,
  error: defaultErrorMstProduct,
  isBeginData: false,
};

const ProductManage = () => {
  const header = ['コード', '名称', '一括割引対象', '保守上限', '使用可', '削除'];
  const [mstProduct, setMstProduct] = useState<MstProduct[]>([]);
  const { confirmAlert, showAlertMessage, registerData } = utility;
  const [removedMstProduct, setRemovedMstProduct] = useState<MstProduct[]>([]);
  const [beginLengthListProduct, setBeginLengthListProduct] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuthenticator((context) => [context.user]);
  const [updateFunc] = useAppMutation({ query: gql(updateMstProduct) });
  const [deleteFunc] = useAppMutation({ query: gql(deleteMstProduct) });
  const [insertFunc] = useAppMutation({ query: gql(insertMstProduct) });
  const [searchMstProduct, { data: mstProductData, loading: isMstProductLoading }] = useAppLazyQuery({
    query: gql(getMstProduct),
    onCompleted: () => {
      setRemovedMstProduct([]);
    },
  });

  const renderTableHeader = useMemo(() => {
    return header.map((key, index) => (
      <th
        className="p-[8px] border-[1px] border-solid border-[#fff] pt-[12px] pb-[12px] text-center bg-[#a8d08d] text-[#fff]"
        key={index}
      >
        {key.toUpperCase()}
      </th>
    ));
  }, []);

  const renderTableData = useMemo(() => {
    if (!mstProduct || mstProduct.length <= 0) return;
    const styleInput = (isError: boolean | undefined) => {
      return `${
        isError ? 'border-[#FF0000] border-[2px] rounded-md' : 'border-{transparent}'
      } pl-[5px] bg-transparent w-full`;
    };
    return mstProduct.map((product: MstProduct, index: number) => {
      const isLastItem = index === mstProduct.length - 1;

      return (
        <tr className="hover:opacity-80 odd:bg-[#e2efd9]" key={index}>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={`${styleInput(product.error.isErrorProductCd)} text-right`}
              onChange={(event) => {
                if (event.target.value.length > 8) return;
                handleChangeProduct(event.target.value, index, 'mpr_product_cd');
              }}
              type="number"
              name="mpr_product_cd"
              min={1}
              max={99999999}
              value={product.mpr_product_cd}
              disabled={product.isCannotEditProductCd}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(product.error.isErrorProductNm)}
              onChange={(event) => handleChangeProduct(event.target.value, index, 'mpr_product_nm')}
              type="text"
              name="name"
              maxLength={50}
              value={product.mpr_product_nm}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <select
              className={styleInput(false)}
              name="discountType"
              onChange={(event) => handleChangeProduct(event.target.value, index, 'mpr_discount_type')}
              value={product.mpr_discount_type ?? ''}
            >
              <option value="0">対象外</option>
              <option value="1">保守料金</option>
              <option value="2">利用料</option>
            </select>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={`${styleInput(product.error.isErrorMaintenanceLimit)} text-right`}
              onChange={(event) => {
                if (event.target.value.length > 8) return;
                handleChangeProduct(event.target.value, index, 'mpr_maintenance_limit');
              }}
              type="number"
              name="maintenanceLimit"
              min={1}
              max={99999999}
              value={product.mpr_maintenance_limit ?? ''}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <select
              className={`${styleInput(false)} text-center`}
              onChange={(event) => handleChangeProduct(event.target.value, index, 'mpr_available_kb')}
              name="availableKb"
              value={product.mpr_available_kb ?? ''}
            >
              <option value="0">×</option>
              <option value="1">○</option>
            </select>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff] text-center">
            <button onClick={() => onPressConfirmRemove(index)}>
              {!isLastItem ? <img src={images.icDelete} alt="" className="w-[20px]" /> : null}
            </button>
          </td>
        </tr>
      );
    });
  }, [mstProduct]);

  const handleChangeProduct = (value: string, index: number, typeProduct: string) => {
    const newListMstProduct = [...mstProduct];
    newListMstProduct[index] = { ...newListMstProduct[index], ...{ [typeProduct]: value } };
    const errorObject: ErrorMstProduct = { ...newListMstProduct[index].error };

    switch (typeProduct) {
      case 'mpr_product_cd':
        errorObject.isErrorProductCd = false;
        break;
      case 'mpr_product_nm':
        errorObject.isErrorProductNm = false;
        break;
      case 'mpr_maintenance_limit':
        errorObject.isErrorMaintenanceLimit = false;
        break;
    }
    newListMstProduct[index].error = { ...newListMstProduct[index].error, ...errorObject };
    if (newListMstProduct[index].isBeginData) {
      newListMstProduct[index].recordState = RecordState.UPDATE;
    }
    setMstProduct(newListMstProduct);
  };

  const isEmptyData = (data: MstProduct) => {
    return (
      data.mpr_product_cd === '' &&
      data.mpr_product_nm === '' &&
      data.mpr_discount_type === null &&
      data.mpr_maintenance_limit === null &&
      data.mpr_available_kb === null
    );
  };

  const onPressRemove = async (index: number) => {
    const newListProduct = [...mstProduct];
    if (index < beginLengthListProduct) {
      const newListRemove = [...removedMstProduct];
      const dataItem = mstProduct[index];
      setBeginLengthListProduct(beginLengthListProduct - 1);
      newListRemove.push({ ...dataItem, recordState: RecordState.DELETE });
      setRemovedMstProduct(newListRemove);
    }
    newListProduct.splice(index, 1);
    setMstProduct(newListProduct);
  };

  const prepareDataApplyApi = (productData: MstProduct) => {
    return {
      mpr_product_cd: productData.mpr_product_cd,
      mpr_product_nm: productData.mpr_product_nm,
      mpr_discount_type: productData.mpr_discount_type,
      mpr_maintenance_limit: productData.mpr_maintenance_limit,
      mpr_available_kb: productData.mpr_available_kb,
    };
  };

  const executeRegister = async () => {
    const mstProductNewList = [...mstProduct];
    const insertData = mstProductNewList.filter((item: MstProduct) => item.recordState === RecordState.INSERT);
    const updateData = mstProductNewList.filter((item: MstProduct) => item.recordState === RecordState.UPDATE);

    // 最終行(新規行)を除外
    insertData.pop();

    const delSet: MutationSet<MstProduct> = {
      recordState: RecordState.DELETE,
      data: removedMstProduct,
      func: (data: MstProduct) => deleteFunc({ variables: { input: { mpr_product_cd: data.mpr_product_cd } } }),
    };
    const updSet: MutationSet<MstProduct> = {
      recordState: RecordState.UPDATE,
      data: updateData,
      func: (data: MstProduct) => updateFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };
    const insSet: MutationSet<MstProduct> = {
      recordState: RecordState.INSERT,
      data: insertData,
      func: (data: MstProduct) => insertFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };

    const searchFunc = () => searchMstProduct({ variables: { input: {} } });

    registerData<MstProduct>({
      mutationSets1: [delSet, updSet, insSet],
      validates,
      searchFunc,
    });
  };

  const onPressConfirmRegister = () => {
    Auth.currentAuthenticatedUser()
      .then(() =>
        confirmAlert(InfoMessage.CONFIRM_REGISTR, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, executeRegister)
      )
      .catch(() => {
        signOut();
        navigate(LOGIN_PATH, { replace: true, state: { from: location } });
      });
  };

  const onPressConfirmRemove = (index: number) => {
    confirmAlert(InfoMessage.CONFIRM_REMOVE, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, () =>
      onPressRemove(index)
    );
  };

  const checkError = (itemProduct: MstProduct) => {
    const isErrorProductCd = itemProduct.mpr_product_cd === '';
    const isErrorProductNm = itemProduct.mpr_product_nm === '';
    const isErrorMaintenanceLimit = itemProduct.mpr_maintenance_limit === null;
    return { isErrorProductCd, isErrorProductNm, isErrorMaintenanceLimit };
  };

  const isExistItem = () => {
    const newListProduct = [...mstProduct];
    const valueArr = newListProduct.map(function (item) {
      return item.mpr_product_cd.toString();
    });

    const isDuplicate = valueArr.find(function (item, idx) {
      if (valueArr.indexOf(item) !== idx) {
        newListProduct[idx].error.isErrorProductCd = true;
        return true;
      }
      return false;
    });
    setMstProduct(newListProduct);
    return isDuplicate;
  };

  const validates = () => {
    const newListProduct = [...mstProduct];
    newListProduct.pop();
    const newListAddError = newListProduct.map((itemProduct: MstProduct) => {
      return { ...itemProduct, error: checkError(itemProduct) };
    });
    setMstProduct(newListAddError);
    const indexItemError = newListProduct.findIndex(
      (itemProduct: MstProduct) =>
        itemProduct.mpr_product_cd === '' ||
        itemProduct.mpr_product_nm === '' ||
        itemProduct.mpr_maintenance_limit === null
    );
    if (indexItemError !== -1) {
      showAlertMessage('', ErrorMessage.INPUT_ERROR, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    if (isExistItem()) {
      showAlertMessage('', InfoMessage.REGISTR_DUPLICATION, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    return true;
  };

  useEffect(() => {
    searchMstProduct({ variables: { input: {} } });
  }, []);

  useEffect(() => {
    if (mstProduct?.length < 1) return;
    const lastIndex = mstProduct.length - 1;
    const newListMstProduct = [...mstProduct];
    if (!isEmptyData(newListMstProduct[lastIndex])) {
      newListMstProduct.push(emptyProduct);
      setMstProduct(newListMstProduct);
    }
  }, [mstProduct]);

  useEffect(() => {
    if (!mstProductData) return;
    const newListProduct = mstProductData.getMstProduct.map((item: MstProduct) => {
      return {
        ...item,
        recordState: RecordState.NONE,
        isCannotEditProductCd: true,
        error: defaultErrorMstProduct,
        isBeginData: true,
      };
    });
    setBeginLengthListProduct(mstProductData.getMstProduct.length);
    newListProduct.sort((a: MstProduct, b: MstProduct) => Number(a.mpr_product_cd) - Number(b.mpr_product_cd));
    newListProduct.push(emptyProduct);
    setMstProduct(newListProduct);
  }, [mstProductData]);

  return (
    <MainLayout>
      <div className="flex flex-col items-center">
        <p className="p-[20px]">製品区分管理</p>
        <div className="flex w-4/5">
          <button
            onClick={onPressConfirmRegister}
            className="border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[100px] font-bold"
          >
            登録
          </button>
        </div>
        <table className="border-collapse border-[3px] border-solid border-[#ddd] w-4/5 td:p-[10px]">
          <thead>
            <tr className="hover:opacity-80 even:bg-[#e2efd9]">{renderTableHeader}</tr>
          </thead>
          <tbody>
            {isMstProductLoading ? (
              <tr className="hover:opacity-80 even:bg-[#e2efd9]">
                <td
                  colSpan={header.length}
                  className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                >
                  Loading...
                </td>
              </tr>
            ) : (
              renderTableData
            )}
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
};

export default ProductManage;
